// components
import Button, { ButtonType } from 'client/components/common/Button/Button'
import keys from 'client/components/txtkeys/subscriptionKeys'
import { Analytics, checkPlacement } from 'client/Global/Analytics'
import { trackingFields } from 'client/Global/AnalyticsEvents'
import { CompanyInterface } from 'client/Interfaces/CompanyInterface'
import { QuotaTypes, SubscriptionInterface, SubscriptionPlanInterface } from 'client/Interfaces/Subscription'
import { formatDate } from 'client/Other/functions/dateFunctions'
import { l } from 'client/Other/Translation'
import { setBaseClass } from 'client/utils'
import * as React from 'react'

import {
  applicableToFreeTrial,
  getIsPlanCancelled,
  isPaidPlan,
  priceAndCurrency,
  shownQuotaAmount,
} from '../subscriptionHelperFunctions'
import FeatureList from './FeatureList/FeatureList'
import {
  IAvailablePlanNames,
  ISubscriptionPremiumPlanOptionsCombinedPropsInterface,
  ISubscriptionPremiumPlanOptionsPropsInterface,
  SubscriptionType,
} from './IPremiumInterface'
// typescript
import './PremiumPlanOption.scss'

const PLAN_CONTACT_US = 'plan.contact_us'

const baseAutomation = 'subscription-option'
const cls = setBaseClass('PremiumPlanOption')

type TPlanAction = Pick<
  ISubscriptionPremiumPlanOptionsPropsInterface,
  'subscription' | 'plan' | 'dateFormat' | 'locale' | 'onCancelDowngrade' | 'onPlanSelection'
>

interface IPlanAction extends TPlanAction {
  planType: number
  subscription: SubscriptionInterface
}

interface IGetPlanProps {
  plan: SubscriptionPlanInterface
  allPlans: SubscriptionPlanInterface[]
  subscription: SubscriptionInterface
  company?: CompanyInterface
}
export const getPlanType = (props: IGetPlanProps): SubscriptionType => {
  const { subscription } = props
  let { plan } = props
  const allPlans = props.allPlans
  const billingCycle = plan.billingCycle

  let isCurrentSubscription = false
  if (subscription && subscription.plan) {
    isCurrentSubscription = subscription.plan.name === plan.name
  }

  const isMyPlanAnnual =
    subscription && subscription.plan && subscription.plan.id && subscription.plan.billingCycle === 'year'
  const isSubscriptionAnnual = plan.billingCycle === 'year'

  let subscriptionPlan = subscription && subscription.plan
  if (isMyPlanAnnual) {
    plan = allPlans.find((p) => p.name === plan.name && p.billingCycle === 'month')
    const currentMonthlySubscriptionPlan = allPlans.find(
      (p) => p.name === subscriptionPlan.name && p.billingCycle === 'month',
    )
    if (currentMonthlySubscriptionPlan) {
      subscriptionPlan = currentMonthlySubscriptionPlan
    } else {
      subscriptionPlan = { ...subscriptionPlan, price: subscriptionPlan.price / 12 }
    }
  }
  if (isSubscriptionAnnual && !isMyPlanAnnual) {
    plan = allPlans.find((p) => p.name === plan.name && p.billingCycle === 'month')
  }
  const fromYearlyToMontly = isMyPlanAnnual && !isSubscriptionAnnual

  const fromMonthlyToYearly = !isMyPlanAnnual && isSubscriptionAnnual
  if (plan === undefined || plan.name.includes('free')) {
    return SubscriptionType.CURRENT_FREE
  }
  if (subscription && subscription.status === 'no_subscription') {
    return billingCycle === 'year' ? SubscriptionType.UPGRADE : SubscriptionType.SUBSCRIBE
  }
  if (!subscription) {
    if (plan.price === 0) {
      return SubscriptionType.CURRENT_FREE
    } else {
      return SubscriptionType.SUBSCRIBE
    }
  } else if (isCurrentSubscription) {
    if (fromYearlyToMontly) {
      return SubscriptionType.CHANGE_TO_MONTHLY_CURRENT
    } else if (fromMonthlyToYearly) {
      return SubscriptionType.CHANGE_TO_YEARLY_CURRENT
    }
    return SubscriptionType.CURRENT
  } else if (subscriptionPlan.price <= plan.price) {
    if (fromYearlyToMontly) {
      return SubscriptionType.CHANGE_TO_MONTHLY_UPGRADE
    } else if (fromMonthlyToYearly) {
      return SubscriptionType.CHANGE_TO_YEARLY_UPGRADE
    }
    return SubscriptionType.UPGRADE
  } else {
    if (fromYearlyToMontly) {
      return SubscriptionType.CHANGE_TO_MONTHLY_DOWNGRADE
    } else if (fromMonthlyToYearly) {
      return SubscriptionType.CHANGE_TO_YEARLY_DOWNGRADE
    }
    return SubscriptionType.DOWNGRADE
  }
}

export function getTranslationWithDate(dateFormat, locale, translationKey: string, dateTs: number): string {
  const translation = l(translationKey)
  return translation.replace('%date', formatDate(new Date(dateTs), dateFormat, locale))
}

export const PlanAction = (props: IPlanAction) => {
  const { subscription, planType, plan, dateFormat, locale, onCancelDowngrade, onPlanSelection } = props
  const planName = plan.name.split('.').pop()

  if (plan.name === PLAN_CONTACT_US) {
    return (
      <div className={cls('__action')}>
        <div className="Button blue">
          <a href={l(keys.contactUsUrl)} className={cls('__link-button')} title={l(keys.contactUs)} target="_blank">
            {l(keys.contactUs)}
          </a>
        </div>
      </div>
    )
  }

  if (subscription && subscription.downgradeTo && plan.id === subscription.downgradeTo) {
    return (
      <div className={cls('__new_plan')}>
        <div className={cls('__new_plan__title')}>{l(keys.premiumPlanModal.newPlan)}</div>
        <div className={cls('__new_plan__starting')}>
          {getTranslationWithDate(dateFormat, locale, keys.premiumPlanModal.planStarting, subscription.downgradeAt)}
        </div>
      </div>
    )
  }

  if (subscription && subscription.downgradeTo && plan.id === subscription.plan.id) {
    return (
      <div className={cls('__current_ending')}>
        <div className={cls('__current_ending__title')}>{l(keys.downgradeCurrentPlan)}</div>
        <div className={cls('__current_ending__status')}>{l(keys.premiumPlanModal.downgraded)}</div>
        <div className={cls('__current_ending__ends')}>
          {getTranslationWithDate(dateFormat, locale, keys.premiumPlanModal.planEnding, subscription.downgradeAt)}
        </div>

        <Button
          className={cls('__current_ending__cancel_link')}
          action={onCancelDowngrade}
          type={ButtonType.link}
          label={l(keys.premiumPlanModal.cancelDowngrade)}
        />
      </div>
    )
  }

  const getSubscribeAct = (applicableForFreeTrial, subscription) => {
    let subscribeAct = ''
    if (applicableForFreeTrial && subscription.status === 'no_subscription') {
      subscribeAct = 'subscribeWithTrial'
    } else if (subscription && subscription.status === 'free') {
      subscribeAct = 're-subscribe'
    } else if (subscription && subscription.plan.price > plan.price) {
      subscribeAct = 'downgrade'
    } else if (applicableForFreeTrial) {
      subscribeAct = 'upgradeWithTrial'
    } else if (subscription.status === 'incomplete') {
      subscribeAct = 'authorizePayment'
    }
    return subscribeAct
  }

  const premiumPlanOptionEvent = () => {
    const applicableForFreeTrial = applicableToFreeTrial(plan, subscription)

    return Analytics.trackEvent(trackingFields.name.premiumPlansModal, {
      type: trackingFields.type.action,
      category: trackingFields.category.subscription,
      result: trackingFields.Modifiers.success,
      placement: checkPlacement(window.location.pathname),
      selectedPlan: {
        id: subscription.plan.id,
        name: subscription.plan.name,
        billingCycle: subscription.plan.billingCycle,
      },
      subscribeAct: getSubscribeAct(applicableForFreeTrial, subscription),
      applicableForFreeTrial,
    })
  }

  const ButtonKeys = {
    [SubscriptionType.CHANGE_TO_MONTHLY_CURRENT]: 'subscription.change-to-m-current',
    [SubscriptionType.CHANGE_TO_MONTHLY_DOWNGRADE]: 'subscription.change-to-m-downgrade',
    [SubscriptionType.CHANGE_TO_MONTHLY_UPGRADE]: 'subscription.change-to-m-upgrade',

    [SubscriptionType.CHANGE_TO_YEARLY_CURRENT]: 'subscription.change-to-y-current',
    [SubscriptionType.CHANGE_TO_YEARLY_DOWNGRADE]: 'subscription.change-to-y-downgrade',
    [SubscriptionType.CHANGE_TO_YEARLY_UPGRADE]: 'subscription.change-to-y-upgrade',

    [SubscriptionType.UPGRADE]: keys.upgrade,
    [SubscriptionType.SUBSCRIBE]: keys.subscribe,
    [SubscriptionType.DOWNGRADE]: keys.downgrade,
  }

  switch (planType) {
    case SubscriptionType.CHANGE_TO_MONTHLY_CURRENT:
    case SubscriptionType.CHANGE_TO_YEARLY_CURRENT:
      return (
        <Button
          automation={`${baseAutomation}__${planName}--upgrade`}
          label={l(ButtonKeys[planType])}
          type={ButtonType.accept}
          action={() => {
            onPlanSelection(props.plan)
            premiumPlanOptionEvent()
          }}
        />
      )

    case SubscriptionType.CHANGE_TO_YEARLY_DOWNGRADE:
    case SubscriptionType.CHANGE_TO_MONTHLY_DOWNGRADE:
    case SubscriptionType.DOWNGRADE:
      return (
        <Button
          automation={`${baseAutomation}__${planName}--downgrade`}
          label={l(ButtonKeys[planType])}
          type={ButtonType.red}
          action={() => {
            onPlanSelection(props.plan)
          }}
        />
      )
    case SubscriptionType.CHANGE_TO_MONTHLY_UPGRADE:
    case SubscriptionType.CHANGE_TO_YEARLY_UPGRADE:
    case SubscriptionType.UPGRADE:
      return (
        <Button
          automation={`${baseAutomation}__${planName}--upgrade`}
          label={l(ButtonKeys[planType])}
          type={ButtonType.accept}
          action={() => {
            onPlanSelection(props.plan)
            premiumPlanOptionEvent()
          }}
        />
      )
    case SubscriptionType.SUBSCRIBE:
      return (
        <Button
          automation={`${baseAutomation}__${planName}--subscribe`}
          label={l(keys.subscribe)}
          type={ButtonType.accept}
          action={() => {
            onPlanSelection(props.plan)
            premiumPlanOptionEvent()
          }}
        />
      )

    case SubscriptionType.CURRENT_FREE:
      return <div className={cls('__action-current')}>{l(keys.currentLabel)}</div>
    default:
      return <div className={cls('__action-current')}>{l(keys.downgradeCurrentPlan)}</div>
  }
}

const getPremiumInvoiceCount = (groupedPlans, allPlans) =>
  groupedPlans.map(({ name, quotas, billingCycle }) => {
    let selectedQuotas

    if (billingCycle === 'year') {
      // if its a yearly combined plan, then pick monthly quota instead (ux reasons)
      const findCurrentPlanMonthlyPlan = allPlans.find((i) => i.name === name)
      selectedQuotas = findCurrentPlanMonthlyPlan.quotas
    } else {
      selectedQuotas = quotas
    }

    const premiumInvoice = selectedQuotas && selectedQuotas.find((i) => i.name === 'premium_invoice')

    return {
      name,
      quota: premiumInvoice && premiumInvoice.quota,
    }
  })

const PremiumPlanGrouped = (props: ISubscriptionPremiumPlanOptionsCombinedPropsInterface) => {
  const {
    groupedPlans,
    allPlans,
    subscription,
    premiumPlansFeatures,
    hideDowngradePlans,
    dateFormat,
    locale,
    onCancelDowngrade,
    onPlanSelection,
    settings,
    isCombinedPlan,
  } = props

  const [selectedPlanName, onSelectCombinedPlan] = React.useState(groupedPlans[0].name)
  if (!groupedPlans.length) {
    return null
  }

  const selectableAvailablePlans = getPremiumInvoiceCount(groupedPlans, allPlans)
  if (!selectableAvailablePlans) {
    return null
  }

  return (
    <PremiumPlanOption
      isCombinedPlan={isCombinedPlan}
      allPlans={allPlans}
      onSelectCombinedPlan={onSelectCombinedPlan}
      selectedPlanName={selectedPlanName}
      plan={groupedPlans.find((i) => i.name === selectedPlanName)}
      subscription={subscription}
      premiumPlansFeatures={premiumPlansFeatures}
      hideDowngradePlans={hideDowngradePlans}
      dateFormat={dateFormat}
      locale={locale}
      onCancelDowngrade={onCancelDowngrade}
      onPlanSelection={onPlanSelection}
      selectableAvailablePlans={selectableAvailablePlans}
      settings={settings}
    />
  )
}

const PremiumPlanOption = (props) => {
  const {
    plan,
    subscription,
    premiumPlansFeatures,
    hideDowngradePlans,
    selectableAvailablePlans,
    onCancelDowngrade,
    onPlanSelection,
    onSelectCombinedPlan,
    isCombinedPlan,
    selectedPlanName,
    dateFormat,
    allPlans,
    locale,
    settings,
  } = props

  if (!plan) {
    return null
  }

  const planType = getPlanType({ plan, subscription, allPlans })
  const hidePlan = planType === SubscriptionType.DOWNGRADE && hideDowngradePlans

  if (hidePlan) {
    return null
  }

  const isRecommended = plan.name === IAvailablePlanNames['plan.pro'] && !isPaidPlan(subscription)
  const isPlanCancelled = subscription && getIsPlanCancelled(subscription)
  const isCurrent = planType === SubscriptionType.CURRENT || planType === SubscriptionType.CURRENT_FREE

  // todo: better this
  const features = premiumPlansFeatures[plan.name]
  features.highlights.forEach((highlight) => {
    highlight.amount = shownQuotaAmount(highlight.id, plan)
  })
  features.items.forEach((item) => {
    if (
      [
        QuotaTypes.CUSTOMERS,
        QuotaTypes.LIMITED_INVOICES_ESTIMATES,
        QuotaTypes.INVOICES_ESTIMATES,
        QuotaTypes.E_INVOICE,
        QuotaTypes.INVOICE,
        QuotaTypes.EXPENSES,
      ].includes(item.id)
    ) {
      item.amount = shownQuotaAmount(item.id, plan)
    }
  })

  const planClassNames = {
    '--option': true,
    '--current': isCurrent && !isPlanCancelled,
  }

  planClassNames[`--plan-${plan.id}`] = true

  const pricePerMonth = plan.billingCycle === 'year' ? plan.price / 12 : plan.price
  const combinedPlansCommonPlanName =
    plan.name === PLAN_CONTACT_US ? l(IAvailablePlanNames['plan.growth']) : l(plan.name)
  let YearlyPriceElement

  if (plan.billingCycle === 'year') {
    // todo: fix this, remove contact us
    if (plan.name === PLAN_CONTACT_US) {
      YearlyPriceElement = <div className={cls('__no-price-spacer')} />
    } else {
      YearlyPriceElement = (
        <div className={cls('__price-label_secondary')}>
          {priceAndCurrency(plan.price, plan.currency)} {l(keys.yearlyPrice)}
        </div>
      )
    }
  }
  return (
    <div className={cls(planClassNames)}>
      {isRecommended && <div className={cls('__recommended-badge')}>{l(keys.mostPopular)}</div>}

      <div className={cls('__name')}>{combinedPlansCommonPlanName}</div>
      {plan.name === PLAN_CONTACT_US ? (
        <div className={cls('__contact-us')}>{l(keys.askForAQuote)}</div>
      ) : (
        <div className={cls('__price')}>{priceAndCurrency(pricePerMonth, plan.currency)}</div>
      )}

      {plan.name !== PLAN_CONTACT_US && <div className={cls('__price-label')}>{l(keys.monthlyPrice)}</div>}

      {YearlyPriceElement}

      <div className={cls('__action')}>
        <PlanAction
          planType={planType}
          subscription={subscription}
          plan={plan}
          onCancelDowngrade={onCancelDowngrade}
          onPlanSelection={onPlanSelection}
          dateFormat={dateFormat}
          locale={locale}
        />
      </div>

      <div className={cls('__features')}>
        <FeatureList
          {...features}
          isCombinedPlan={isCombinedPlan}
          selectedPlanName={selectedPlanName}
          selectableAvailablePlans={selectableAvailablePlans}
          onSelectCombinedPlan={onSelectCombinedPlan}
          billingCycle={plan.billingCycle}
          settings={settings}
          allPlans={allPlans}
          processingPlanName={plan.name}
        />
      </div>
    </div>
  )
}

export { PremiumPlanGrouped, getPremiumInvoiceCount, PremiumPlanOption }
